import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  signal,
  inject
} from '@angular/core';
import { Router } from '@angular/router';

import { FronteggAuthService } from '@frontegg/angular';
import { SisErrorCodes } from '../../../models/sis-response.model';
import { Subscription } from 'rxjs';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { MessageService, PrimeTemplate } from 'primeng/api';

import { TranscriptMetadata } from '../../../models/documentResponse';
import { TranscriptToExport } from '../../../models/etrive.model';

import { AuthService } from '../../../services/auth/auth.service';
import {
  FileUploadResult,
  FileUploadService
} from '../../../services/file-upload/file-upload.service';
import { ToastService } from '../../../services/toast-messages/toast-messages.service';
import { TranscriptExportService } from '../../../services/transcript-export/transcript-export.service';
import { TranscriptMappingService } from '../../../services/transcript-data-mapper/transcript-data-mapper.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { TranscriptMetadataService } from '../../../services/transcript-metadata/transcript-metadata.service';
import { TranscriptRealtimeService } from '../../../services/transcript-realtime/transcript-realtime.service';
import { TranscriptStatus, TranscriptStatusLabels, TranscriptType } from '../../../constants';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { TranscriptTableComponent } from '../components/transcript-table/transcript-table.component';
import { Toast } from 'primeng/toast';
import { WebScanningDialogComponent } from '../../../components/web-scanning-dialog/web-scanning-dialog.component';
import { TranscriptUploadDialogComponent } from '../components/transcript-upload-dialog/transcript-upload-dialog.component';
import { getProcessorRoleInfo } from '../../../utils/itp-processor-role.util';

interface TranscriptQueueTypeLabels {
  headerKey: string;
  selectTranscriptsButtonKey: string;
  scanButtonKey: string;
}

const DEFAULT_TRANSCRIPT_LABELS: TranscriptQueueTypeLabels = {
  headerKey: 'ITP.transcriptQueue',
  selectTranscriptsButtonKey: 'ITP.selectTranscripts',
  scanButtonKey: 'ITP.scanTranscripts'
};

const TRANSCRIPT_TYPE_LABELS: Record<TranscriptType, TranscriptQueueTypeLabels> = {
  [TranscriptType.HighSchool]: {
    headerKey: 'ITP.hsTranscriptQueue',
    selectTranscriptsButtonKey: 'ITP.selectHsTranscripts',
    scanButtonKey: 'ITP.scanHsTranscripts'
  },
  [TranscriptType.College]: {
    headerKey: 'ITP.collegeTranscriptQueue',
    selectTranscriptsButtonKey: 'ITP.selectCollegeTranscripts',
    scanButtonKey: 'ITP.scanCollegeTranscripts'
  }
};

/**
 * Gets the labels for the filtered to "type" or uses defaults
 * @param type - The active transcript type filter, or null when unfiltered
 */
function getTranscriptTypeLabels(type: TranscriptType | null): TranscriptQueueTypeLabels {
  if (type === null) {
    return DEFAULT_TRANSCRIPT_LABELS;
  }

  return TRANSCRIPT_TYPE_LABELS[type];
}

@Component({
  selector: 'app-transcript-queue',
  templateUrl: './transcript-queue.component.html',
  styleUrl: './transcript-queue.component.scss',
  imports: [
    Bind,
    Button,
    TranscriptTableComponent,
    Toast,
    PrimeTemplate,
    WebScanningDialogComponent,
    TranscriptUploadDialogComponent,
    TranslatePipe
  ]
})
export class TranscriptQueueComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private messageService = inject(MessageService);
  private toastService = inject(ToastService);
  private authService = inject(AuthService);
  private fronteggAuthService = inject(FronteggAuthService);
  private transcriptMetadataService = inject(TranscriptMetadataService);
  private transcriptExportService = inject(TranscriptExportService);
  private transcriptMappingService = inject(TranscriptMappingService);
  private translateService = inject(TranslateService);
  private featureFlagService = inject(FeatureFlagService);
  private transcriptRealtimeService = inject(TranscriptRealtimeService);
  private fileUploadService = inject(FileUploadService);

  @ViewChild('transcriptTable') transcriptTable!: any;

  itpTypeFilterFlag = signal(false);
  isItpPowerProcessor = signal(false);

  activeTypeFilter = signal<TranscriptType | null>(
    (() => {
      const storedTypeFilter = sessionStorage.getItem('transcriptTypeFilter');
      return storedTypeFilter !== null ? Number(storedTypeFilter) : null;
    })()
  );
  transcripts = signal<TranscriptMetadata[]>([]);
  isLoadingTranscripts = signal<boolean>(true);

  transcriptTypeLabels = computed(() => getTranscriptTypeLabels(this.activeTypeFilter()));

  /**
   * Transcripts scoped to the active type filter.
   */
  visibleTranscripts = computed(() => {
    const typeFilter = this.activeTypeFilter();
    return typeFilter === null
      ? this.transcripts()
      : this.transcripts().filter(doc => doc.typeId === typeFilter);
  });

  private statusCounts = computed(() => {
    const currentUser = this.fronteggAuthService.user;
    const displayName = (currentUser as any)?.customClaims?.display_name || currentUser?.name || '';

    const transcripts = this.visibleTranscripts();

    return transcripts.reduce(
      (acc, doc) => {
        const status = doc.extractionStatus ?? 'Unknown';
        acc[status] = (acc[status] || 0) + 1;
        if (doc.lockedByUser === displayName) acc['assignedToMe']++;
        return acc;
      },
      { assignedToMe: 0 } as Record<string, number>
    );
  });

  myAssignmentsCount = computed(() => this.statusCounts()['assignedToMe'] || 0);
  readyForExportCount = computed(() => this.statusCounts()['Ready for Export'] || 0);
  archivedCount = computed(() => this.statusCounts()['Archived'] || 0);
  needsReviewCount = computed(() => this.statusCounts()['Needs Review'] || 0);
  exportedCount = computed(() => this.statusCounts()['Exported'] || 0);

  private readonly typeFilterEffect = effect(() => {
    const typeFilter = this.activeTypeFilter();
    if (typeFilter === null) {
      sessionStorage.removeItem('transcriptTypeFilter');
      return;
    }

    sessionStorage.setItem('transcriptTypeFilter', String(typeFilter));
  });

  tenantId: string = '';
  showScanDialog = false;
  showUploadDialog = false;
  isDragOver = false;

  private authSubscription?: Subscription;
  private transcriptStateSubscription?: Subscription;
  private singleTypeProcessor: TranscriptType | null = null;

  /**
   * Initializes the component by checking authentication, loading documents, and setting up real-time updates
   */
  ngOnInit(): void {
    this.authSubscription = this.fronteggAuthService.authState$.subscribe(
      async authState => {
        const hasItpProcessorRole = !!authState.user?.roles?.some(
          (role: any) => role.key === 'ITP Processor' || role.key === 'ITP HS Processor'
        );
        if (!hasItpProcessorRole) {
          this.router.navigate(['unauthorized']);
          return;
        }
        const { singleType, isPowerProcessor } = getProcessorRoleInfo(authState.user?.roles);
        this.singleTypeProcessor = singleType;
        if (this.activeTypeFilter() === null && singleType !== null) {
          this.activeTypeFilter.set(singleType);
        }
        this.isItpPowerProcessor.set(isPowerProcessor);
        this.transcriptRealtimeService.setAllowedTranscriptType(
          isPowerProcessor ? null : singleType
        );
        if (authState.user?.accessToken) {
          this.authService.setAuthToken(authState.user.accessToken);
        }
        this.initializeTenantId();
        try {
          await this.transcriptRealtimeService.connect();
        } catch (error) {
          console.error('Failed to connect transcript realtime service:', error);
        }
        this.setupStateSubscription();
        this.loadDocuments();
      },
      () => {
        this.router.navigate(['unauthorized']);
      }
    );

    this.featureFlagService
      .getFlag('feature-itp-high-school-transcripts', false)
      .subscribe(flag => {
        this.itpTypeFilterFlag.set(flag);
      });
  }

  /**
   * Cleans up subscriptions
   */
  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.transcriptStateSubscription?.unsubscribe();
    this.transcriptRealtimeService.disconnect();
  }

  /**
   * Initializes the tenant ID for the current user
   */
  initializeTenantId(): void {
    const user = this.fronteggAuthService.user;
    this.tenantId = user?.tenantId || '';
  }

  /**
   * Loads all documents and initializes the transcript table
   */
  async loadDocuments(): Promise<void> {
    const existing = this.transcriptRealtimeService.getTranscripts();
    if (existing.length > 0) {
      this.transcripts.set(existing);
      this.isLoadingTranscripts.set(false);
      return;
    }

    this.isLoadingTranscripts.set(true);
    const documents = await this.transcriptMetadataService.getAllDocuments();
    if (!Array.isArray(documents)) {
      this.transcripts.set([]);
      this.transcriptRealtimeService.setTranscripts([]);
      this.isLoadingTranscripts.set(false);
      return;
    }

    const sortedDocuments = documents.sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    const mappedDocuments = sortedDocuments.map(doc => ({
      documentId: doc.documentId,
      transId: doc.transId,
      fileName: doc.fileName,
      createdAt: doc.createdAt,
      contentUri: doc.contentUri || '',
      updatedAt: doc.updatedAt,
      version: 1,
      contentMetadata: null,
      extractionStatus:
        TranscriptStatusLabels[doc.statusId as TranscriptStatus] ||
        TranscriptStatusLabels[TranscriptStatus.PendingExtraction],
      transcriptData: undefined,
      student_name: `${doc.firstName || ''} ${doc.lastName || ''}`.trim(),
      confidence_score:
        doc.confidenceScore !== null && doc.confidenceScore !== undefined
          ? `${Math.round(doc.confidenceScore * 100)}%`
          : '',
      lockedByUser: doc.lockedByUserName || null,
      typeId: doc.typeId,
      typeMismatch: doc.typeMismatch
    }));

    this.transcriptRealtimeService.setTranscripts(mappedDocuments);
    this.isLoadingTranscripts.set(false);
  }

  /**
   * Subscribe to transcript state changes from the service
   */
  private setupStateSubscription(): void {
    this.transcriptStateSubscription = this.transcriptRealtimeService.transcripts$.subscribe(
      transcripts => {
        this.transcripts.set(transcripts);
      }
    );
  }

  /**
   * Handles file selection from input or drop zone
   * @param files - Array of File objects to be uploaded
   */
  onFilesSelected(files: File[]): void {
    this.fileUploadService
      .uploadFiles(files, this.tenantId, true, this.activeTypeFilter())
      .subscribe({
        next: result => this.handleUploadResult(result),
        error: error => this.handleDocumentUploadError(error)
      });
  }

  /**
   * Handles file input change events from the file input element
   * @param event - The file input change event
   */
  onFileInputChange(event: Event): void {
    this.fileUploadService
      .handleFileInputChange(event, this.tenantId, true, this.activeTypeFilter())
      .subscribe({
        next: result => this.handleUploadResult(result),
        error: error => this.handleDocumentUploadError(error)
      });
  }

  /**
   * Handles file drop events from the drop zone
   * @param event - The drag event containing dropped files
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.fileUploadService
      .handleDrop(event, this.tenantId, true, this.activeTypeFilter())
      .subscribe({
        next: result => this.handleUploadResult(result),
        error: error => this.handleDocumentUploadError(error)
      });
  }

  /**
   * Handles drag over events
   * @param event - The drag event to process
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  /**
   * Handles drag leave events
   * @param event - The drag event to process
   */
  onDragLeave(event: DragEvent): void {
    this.isDragOver = false;
  }

  /**
   * Handles starting the scan process
   */
  startScanProcess(): void {
    this.showScanDialog = true;
  }

  /**
   * Handles files received from the scanning process
   * @param files - Array of scanned File objects to be processed
   */
  onScannedFiles(files: File[]): void {
    this.onFilesSelected(files);
  }

  /**
   * Handles the result of a file upload operation
   * @param result - The file upload result containing statuses and uploaded IDs
   */
  private handleUploadResult(result: FileUploadResult): void {
    result.statuses.forEach(status => {
      if (status.status === 'failed' && status.error) {
        this.handleDocumentUploadError(status.error);
      } else if (status.status === 'completed' && status.id) {
        const exists = this.transcriptRealtimeService
          .getTranscripts()
          .some(t => t.transId === status.id);
        if (!exists) {
          this.transcriptRealtimeService.setTranscripts([
            ...this.transcriptRealtimeService.getTranscripts(),
            {
              transId: status.id,
              fileName: status.file.name,
              extractionStatus: 'Pending Extraction',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              contentUri: '',
              student_name: '',
              confidence_score: '',
              lockedByUser: null
            }
          ]);
        } else {
          this.transcriptRealtimeService.updateTranscript(status.id, {
            fileName: status.file.name
          });
        }
      }
    });
  }

  /**
   * Handles errors during document upload
   * @param error - The error message to display
   */
  handleDocumentUploadError(error: string) {
    this.showErrorToast(error);
  }

  /**
   * Handles document deletion
   * @param document - The document to be deleted
   */
  async onTranscriptDeleted(document: TranscriptMetadata): Promise<void> {
    try {
      await this.transcriptMetadataService.deleteDocument(document.transId);
      if (document.transId) {
        this.transcriptRealtimeService.removeTranscript(document.transId);
      }
    } catch (error) {
      this.toastService.error(
        'transcriptErrors.deleteError',
        'transcriptErrors.deleteLockedDocument'
      );
    }
  }

  /**
   * Handles document selection and navigation to transcript upload page
   * @param document - The selected document metadata
   */
  async onTranscriptSelected(document: TranscriptMetadata): Promise<void> {
    this.router.navigate(['transcripts/transcript-upload'], {
      queryParams: { id: document.transId },
      state: { transId: document.transId, documents: this.transcripts() }
    });
  }

  /**
   * Handles document assignment to the current user
   * @param documents - The documents to assign
   */
  async onTranscriptsAssigned(documents: TranscriptMetadata[]): Promise<void> {
    const currentUser = this.fronteggAuthService.user;
    const displayName = (currentUser as any)?.customClaims?.display_name || currentUser?.name || '';

    const lockPromises = documents.map(document =>
      this.transcriptMetadataService
        .lockTranscript(document.transId)
        .then(success => ({ document, success }))
        .catch(error => ({ document, success: false, error }))
    );

    const results = await Promise.allSettled(lockPromises);

    const successfulLocks: string[] = [];
    results.forEach(result => {
      if (result.status === 'fulfilled' && result.value.success) {
        successfulLocks.push(result.value.document.transId);
      } else if (result.status === 'fulfilled' && !result.value.success) {
        console.error(
          'Failed to assign document:',
          result.value.document.transId,
          (result.value as any).error
        );
      }
    });

    if (successfulLocks.length > 0) {
      successfulLocks.forEach(transcriptId => {
        this.transcriptRealtimeService.updateTranscript(transcriptId, {
          lockedByUser: displayName
        });
      });
    }
  }

  /**
   * Handles document unassignment from the current user
   * @param documents - The documents to unassign
   */
  async onTranscriptsUnassign(documents: TranscriptMetadata[]): Promise<void> {
    const unlockPromises = documents.map(document =>
      this.transcriptMetadataService
        .unlockTranscript(document.transId)
        .then(success => ({ document, success }))
        .catch(error => ({ document, success: false, error }))
    );

    const results = await Promise.allSettled(unlockPromises);
    const successfulUnLocks: string[] = [];
    results.forEach(result => {
      if (result.status === 'fulfilled' && result.value.success) {
        successfulUnLocks.push(result.value.document.transId);
      } else if (result.status === 'fulfilled' && !result.value.success) {
        console.error(
          'Failed to unassign document:',
          result.value.document.transId,
          (result.value as any).error
        );
      }
    });

    if (successfulUnLocks.length > 0) {
      successfulUnLocks.forEach(transcriptId => {
        this.transcriptRealtimeService.updateTranscript(transcriptId, {
          lockedByUser: null
        });
      });
    }
  }

  /**
   * Sends multiple transcripts to SIS in batch
   * @param documents - The transcript documents to send to SIS
   */
  async onTranscriptsSendToSis(documents: TranscriptMetadata[]): Promise<void> {
    if (!documents || documents.length === 0) {
      console.warn('No transcripts to export');
      return;
    }

    try {
      const transcriptsToSend: TranscriptToExport[] = [];
      const failedToLoad: string[] = [];

      for (const document of documents) {
        try {
          const extractionResponse = await this.transcriptMetadataService.loadExtraction(
            document.transId
          );

          if (!extractionResponse) {
            console.warn(`No response for transcript ${document.transId}`);
            failedToLoad.push(document.transId);
            continue;
          }

          const content =
            extractionResponse.savedData ||
            extractionResponse.origData ||
            extractionResponse.content;

          if (content && content !== '{}') {
            const rawData = typeof content === 'string' ? JSON.parse(content) : content;
            const transcriptType: TranscriptType = extractionResponse.typeId ?? 0;
            const mappedData = this.transcriptMappingService.mapToTranscriptData(
              rawData,
              transcriptType
            );

            transcriptsToSend.push({
              data: mappedData,
              transcriptId: document.transId,
              transcriptUrl: `/transcripts/transcript-upload?id=${document.transId}`,
              transcriptType: transcriptType
            });
          } else {
            console.warn(`No valid data for transcript ${document.transId}`);
            failedToLoad.push(document.transId);
          }
        } catch (error) {
          console.error(`Failed to load transcript ${document.transId}:`, error);
          failedToLoad.push(document.transId);
        }
      }

      if (failedToLoad.length > 0) {
        console.warn(`Failed to load ${failedToLoad.length} transcript(s):`, failedToLoad);
      }

      if (transcriptsToSend.length === 0) {
        console.error('No valid transcripts to export');
        return;
      }

      const responses = await this.transcriptExportService.sendToSis(transcriptsToSend);

      responses.forEach(response => {
        const isMatchingError =
          response.code === SisErrorCodes.STUDENT_NOT_FOUND ||
          response.code === SisErrorCodes.INSTITUTION_NOT_FOUND ||
          response.code === SisErrorCodes.MULTIPLE_STUDENTS_MATCHED ||
          response.code === SisErrorCodes.MULTIPLE_INSTITUTIONS_MATCHED;
        const isPartial = response.code === SisErrorCodes.PARTIAL_COURSE_UPLOAD_FAILURE;
        const failStatus = isPartial
          ? 'Partially Exported'
          : isMatchingError
            ? 'Failed Matching'
            : 'Failed to Export';
        this.transcripts.update(transcripts =>
          transcripts.map(t =>
            t.transId === response.transcriptId
              ? { ...t, extractionStatus: response.success ? 'Exported' : failStatus }
              : t
          )
        );
        if (response.transcriptId) {
          this.transcriptRealtimeService.updateTranscript(response.transcriptId, {
            extractionStatus: response.success ? 'Exported' : failStatus
          });
        }
      });

      const partial = responses.filter(
        r => r.code === SisErrorCodes.PARTIAL_COURSE_UPLOAD_FAILURE
      ).length;
      const failed = responses.filter(
        r => !r.success && r.code !== SisErrorCodes.PARTIAL_COURSE_UPLOAD_FAILURE
      ).length;

      if (partial > 0) {
        this.toastService.warn(
          'ITP.sisErrors.partialCourseUpload',
          this.translateService.instant('ITP.bulkPartialExportMessage', {
            partial,
            total: responses.length
          }),
          15000
        );
      }

      if (failed > 0) {
        const failedTranscripts = responses
          .filter(r => !r.success)
          .map(r => ({
            id: r.transcriptId,
            code: r.code,
            message: r.message
          }));
        console.error('Failed to export transcripts:', failedTranscripts);
      }
    } catch (error) {
      console.error('Batch send to SIS failed:', error);
    }
  }

  /**
   * Filters transcripts by their transcript type (High School or College)
   * @param type - The transcript type to filter by
   */
  filterByType(type: TranscriptType): void {
    this.activeTypeFilter.set(this.activeTypeFilter() === type ? null : type);
    this.clearAllTableFilters();
  }

  /**
   * Clears custom filters and resets to default view
   */
  clearCustomFilters(): void {
    this.activeTypeFilter.set(this.singleTypeProcessor);
    this.clearAllTableFilters();
  }

  /**
   * Clears all filters from the transcript table except for the global filter, and resets selection
   */
  private clearAllTableFilters(): void {
    this.transcriptTable.resetColumnFilters();
    this.transcriptTable.dt.selection = null;
    this.transcriptTable.selectedDocuments = [];
  }

  /**
   * Shows an error toast message
   * @param toastMessage - The error message to display
   * @param toastData - Additional data to display in the toast
   */
  showErrorToast(toastMessage: string, toastData?: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: toastMessage,
      key: 'errorToast',
      data: toastData || '',
      life: 10000
    });
  }
}
